import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useOutletContext } from "react-router-dom";

import type { ShellOutletContext } from "../nav/AppShell";
import { MasterDetailLayout } from "../components/shell/MasterDetailLayout";
import { IncidentDetailPanel } from "../surfaces/IncidentDetail";
import { IncidentFeed } from "../surfaces/IncidentFeed";
import { useSupportSession } from "../support/SupportSessionProvider";

// Incidents - management view over the hosted incident read models. Guards file incidents
// from the field apps; this page is where managers see them with safe context labels and
// evidence summaries. Review and lock actions (PRD-001AB S7) are gated on evidence.manage,
// which admins and supervisors hold and support does not. Console evidence upload and CSV
// export remain out of scope.
//
// The duty activity report is a client artifact over one approved service week, not over one
// incident, so it is composed on Reports. This page only offers the way in. It carries no
// client, site or week with it: the incident list read model exposes neither the client nor
// the site id, and a prefill guessed from a label would be a claim this page cannot support.
//
// Shell v3 (product lock 2026-08-15, lock 2): master-detail. The queue owns the list pane,
// the record owns the wide pane (it replaced the old overlay drawer), and at 767px and
// below the record pushes over the queue with a back affordance. With no selection the
// record pane ghosts the template so a first-day admin learns the layout before the first
// filing (board Implement rule 9).
export function IncidentsPage() {
  const { client, config, accountRole, can } = useOutletContext<ShellOutletContext>();
  const support = useSupportSession();
  // Mirrors the Reports gate exactly. A support session never sees a client-artifact entry.
  const canExportDutyActivity = (accountRole === "admin" || accountRole === "supervisor")
    && ["report.view", "schedule.view", "evidence.view", "timeclock.manage"].every((permission) => can(permission))
    && !support.loading && support.session === null;
  // The list stays the source of truth for the record. The detail holds only the id it was
  // opened with, so a row that leaves the list resolves to the honest unavailable state
  // rather than to a stale copy the pane keeps showing.
  //
  // The id is kept WITH the account it was opened under. The shell swaps config.accountId
  // under this page without remounting it (workspace switch, support session starting or
  // ending), and an id from the previous workspace has no meaning in the new one.
  //
  // Command Center's Call receipts link here with the report to select. It rides in router state
  // rather than the URL so no incident UUID lands in the address bar.
  //
  // The state carries the workspace it was minted under, and the seed refuses anything else.
  // History state outlives a workspace switch: open a receipt in A, switch to B, then press Back
  // and the router replays that entry with A's id. Without the account check the seed would stamp
  // it as B's. It would still fail closed, because the list read is account-scoped, but the
  // selection an operator sees should never have come from another workspace at all.
  const location = useLocation();
  const linked = location.state as { accountId?: unknown; incidentId?: unknown } | null;
  const requestedIncidentId =
    typeof linked?.incidentId === "string" && linked.accountId === config.accountId
      ? linked.incidentId
      : null;
  const [open, setOpen] = useState<{ accountId: string; incidentId: string } | null>(() =>
    requestedIncidentId === null
      ? null
      : { accountId: config.accountId, incidentId: requestedIncidentId },
  );
  const openId = open !== null && open.accountId === config.accountId ? open.incidentId : null;
  // Only a real workspace *change* clears the selection. Running on mount as well would discard
  // the seed above before it ever rendered; `openId` already refuses a foreign-account id.
  const seenAccountId = useRef(config.accountId);
  useEffect(() => {
    if (seenAccountId.current === config.accountId) return;
    seenAccountId.current = config.accountId;
    setOpen(null);
  }, [config.accountId]);
  const canManage = can("evidence.manage");
  const scopeToken = `${config.accountId}:${support.loading ? "loading" : support.session?.sessionId ?? "direct"}`;

  return (
    <MasterDetailLayout
      className="incidents-mdl"
      detailOpen={openId !== null}
      list={
        <>
          <div className="incidents-pane__head">
            <h1 className="page__title">Incidents</h1>
            <p className="page__note">
              Field incidents with status, severity, and evidence summaries. Evidence listings
              are metadata only, and opening media is a separate audited step. Supervisors and
              admins can move a submitted incident under review and lock it once the record is
              complete.
            </p>
            {canExportDutyActivity && (
              <p className="incidents-pane__artifact-link">
                <Link to="/reports">Export duty activity report</Link> for an approved service
                week. Choose the client, site, and week on Reports.
              </p>
            )}
          </div>
          <div className="incidents-pane__scroll">
            <IncidentFeed
              client={client}
              accountId={config.accountId}
              timeZone={config.displayTimeZone}
              canManage={canManage}
              activeIncidentId={openId}
              scopeToken={scopeToken}
              onScopeChange={() => setOpen(null)}
              emptyAction={
                <Link className="empty-hero__primary" to="/">
                  Open Command Center
                </Link>
              }
              onOpenIncident={(incident) =>
                setOpen({ accountId: config.accountId, incidentId: incident.incidentId })
              }
            />
          </div>
        </>
      }
      detail={
        <>
          <button type="button" className="mdl-back" onClick={() => setOpen(null)}>
            Back to incidents
          </button>
          {openId !== null ? (
            // Keyed by the selection so the record cross-fades (locked 140ms, opacity only).
            <div key={openId} className="incidents-pane__scroll incidents-pane__scroll--detail mo-detail-enter">
              <IncidentDetailPanel
                client={client}
                accountId={config.accountId}
                incidentId={openId}
                timeZone={config.displayTimeZone}
                canManage={canManage}
              />
            </div>
          ) : (
            <div className="incidents-pane__scroll incidents-pane__scroll--detail">
              <IncidentRecordGhost />
            </div>
          )}
        </>
      }
    />
  );
}

/** Dashed skeleton of the filled record (board Implement rule 9): the ghost teaches the
 *  template, and the copy names the Guard app as the source of truth. Renders for both the
 *  zero-records state and the no-selection state, because both are honest answers to "what
 *  appears here". */
function IncidentRecordGhost() {
  return (
    <div className="ghost-record">
      <p className="ghost-record__label">Record · summary · evidence · review and lock</p>
      <p className="ghost-record__hint" role="status">
        Select an incident from the queue to open its record here. Reports filed from the
        Guard app arrive with the filing officer, timestamps, and evidence metadata, then
        move through review to a locked state.
      </p>
      <div className="ghost-record__tiles" aria-hidden="true">
        <span className="ghost-record__tile" />
        <span className="ghost-record__tile" />
        <span className="ghost-record__tile" />
        <span className="ghost-record__tile ghost-record__tile--labeled">evidence</span>
      </div>
      <div className="ghost-record__rows" aria-hidden="true">
        <div className="ghost-row">
          <span className="ghost-label">status</span>
          <span className="ghost-pill" />
          <span className="ghost-label">severity</span>
          <span className="ghost-pill" />
        </div>
        <div className="ghost-row">
          <span className="ghost-label">review</span>
          <span className="ghost-bar" style={{ width: "34%" }} />
          <span className="ghost-label ghost-label--end">lock</span>
        </div>
      </div>
    </div>
  );
}
